import { useDataRefreshInfo, useLoadTranslations } from "@apis/config";
import { useFiltersData } from "@apis/filtersData";
import { useGetGlobalData } from "@apis/globalData";
import { useNavigationMenu } from "@apis/navigationMenu";
import { useOnboardingData } from "@apis/onboarding";
import { useOrgSettings } from "@apis/orgSettings";
import { usePdpLayoutData } from "@apis/pdpCustomization";
import ApiRoutes from "@apis/routes";
import { useSearchResultsCustomization } from "@apis/searchResultsCustomization";
import { useThemeTokens } from "@apis/themeTokens";
import initializeI18n from "@i18n/index";
import Routes from "@navigations/Routes";
import { setAppInitialLoad, setAppVersion } from "@redux/slices/appStateSlice";
import { useNavigationMenuSlice } from "@redux/slices/navigationMenuSlice";
import { useOnboardingDataSlice } from "@redux/slices/OnboardingDataSlice";
import { usePDPSlice } from "@redux/slices/pdpLayoutSlice";
import { useSearchFiltersSlice } from "@redux/slices/searchFilters";
import { useCustomSearchData } from "@redux/slices/searchResultsCustomization";
import { useThemeStore } from "@redux/slices/themeSlice";
import {
  DEFAULT_TRANSLATION_KEYS,
  useTranslationKeys,
} from "@redux/slices/translationSlice";
import { setIsGuest, useUserInfo } from "@redux/slices/userInfoSlice";
import {
  AppStateProps,
  OnboardingScreenPlatform,
  OnBoardingTypes,
  ThemeTokenTypes,
} from "@scenes/splash-screen/types";
import { PLATFORM } from "@utils/index";
import { useFontContext } from "../../contexts/FontContext";
import { getLocales } from "expo-localization";
import * as SplashScreenExpo from "expo-splash-screen";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import useDeviceType from "../useDeviceType";
import { isRefetchRequired } from "./utils";
import { useOrientationLock } from "../useOreintaionLock";
import { useGlobalData } from "@redux/slices/globalSlice";
import { useTheme } from "styled-components/native";
import { useUnifiedProductPagesSlice } from "@redux/slices/unifiedProductPages";
import { useUnifiedProductPagesData } from "@apis/useUnifiedProductPagesData";
import { useInitialUrl } from "@utils/useInitialUrl";
import Constants from "expo-constants";
import { setUserInfo } from "@redux/slices/userInfoSlice";
import { Platform } from "react-native";
import AsyncStorage from "@react-native-async-storage/async-storage";
import { STORAGE_KEYS } from "@utils/constants";

export const useBootstrap = (
  appStateReady: AppStateProps,
  setInitialScreen: (route: Routes) => void,
  setAppStateReady: (appStateProps: AppStateProps) => void
) => {
  const dispatch = useDispatch();
  const { savePreOnboardingData } = useOnboardingDataSlice();
  const onPreBoardingComplete = useSelector(
    (state: any) => state.appState?.onPreBoardingComplete
  );
  const isAppInitialLoad = useSelector(
    (state: any) => state.appState?.isAppInitialLoad
  );
  const appVersion = useSelector((state: any) => state.appState?.appVersion);
  const userInfo = useUserInfo();
  const lockOrientation = useOrientationLock();

  const dataRefreshInfo = useDataRefreshInfo();
  const orgSettings = useOrgSettings(PLATFORM);
  const {
    custom_texts,
    mobile_token_file,
    web_token_file,
    json_navigation_menus,
    pdps,
    search_forms,
    search_results,
    product_unified_pages,
    global,
  } = orgSettings.data?.data?.init_status || {};

  const [enabledRoutes, setEnabledRoutes] = useState({
    custom_texts: false,
    mobile_token_file: false,
    web_token_file: false,
    json_navigation_menus: false,
    pdps: false,
    search_forms: false,
    search_results: false,
    global: false,
    product_unified_pages: false,
  });

  const filtersData = useFiltersData(
    userInfo.getUserId() && userInfo.user.search_forms
      ? userInfo.user.search_forms
      : search_forms || ApiRoutes.SEARCH_FORM_API,
    !!enabledRoutes.search_forms,
    userInfo.getUserId() && userInfo.user.search_forms
      ? ThemeTokenTypes.COMPANY
      : ThemeTokenTypes.ORG
  );
  //useMenuConfigurationsData();

  const searchResultsCustomization = useSearchResultsCustomization(
    userInfo.getUserId() && userInfo.user.search_results
      ? userInfo.user.search_results
      : search_results || ApiRoutes.SEARCH_RESULT_API,
    !!enabledRoutes.search_results,
    userInfo.getUserId() && userInfo.user.search_results
      ? ThemeTokenTypes.COMPANY
      : ThemeTokenTypes.ORG
  );

  const { fontsLoaded, fontError } = useFontContext();

  const fontsReadyForBootstrap = fontsLoaded;
  if (fontError) {
    // Log without exposing error details (fail-safe: app continues with fallback fonts)
    if (__DEV__) {
      console.warn("Font loading failed or timed out; using fallback fonts.");
    }
  }

  const { isTablet, isLargeScreen, isMobile } = useDeviceType();
  const { i18n } = useTranslation();

  const themeTokensSlice = useThemeStore();
  const navigationMenuSlice = useNavigationMenuSlice();
  const globalSlice = useGlobalData();
  const translationSlice = useTranslationKeys();
  const pdpSlice = usePDPSlice();
  const theme = useTheme();

  const searchFormsSlice = useSearchFiltersSlice();
  const searchResultsSlice = useCustomSearchData();
  const unifiedProductPagesSlice = useUnifiedProductPagesSlice();
  const { initialUrl } = useInitialUrl();

  const [enabledChecked, setEnabledChecked] = useState(false);
  const themeTokensApi = useThemeTokens(
    userInfo.getUserId() && userInfo.user.custom_theme
      ? theme.isLargeScreen
        ? userInfo.user.web_token_file
        : userInfo.user.mobile_token_file
      : theme.isLargeScreen
      ? web_token_file
      : mobile_token_file,
    theme.isLargeScreen
      ? !!enabledRoutes.web_token_file
      : !!enabledRoutes.mobile_token_file,
    userInfo.getUserId() && userInfo.user.custom_theme
      ? ThemeTokenTypes.COMPANY
      : ThemeTokenTypes.ORG
  );
  const navigationMenuApi = useNavigationMenu(
    userInfo.getUserId() && userInfo.user.json_navigation_menus
      ? userInfo.user.json_navigation_menus
      : json_navigation_menus || ApiRoutes.NAVIGATION_MENU,
    !!enabledRoutes.json_navigation_menus,
    userInfo.getUserId() && userInfo.user.json_navigation_menus
      ? ThemeTokenTypes.COMPANY
      : ThemeTokenTypes.ORG
  );
  const globalData = useGetGlobalData(
    userInfo.getUserId() && userInfo.user.global
      ? userInfo.user.global
      : global || ApiRoutes.GLOBAL,
    !!enabledRoutes.global,
    userInfo.getUserId() && userInfo.user.global
      ? ThemeTokenTypes.COMPANY
      : ThemeTokenTypes.ORG
  );

  const translationApi = useLoadTranslations(
    userInfo.getUserId() && userInfo.user.custom_texts
      ? userInfo.user.custom_texts
      : custom_texts || ApiRoutes.TRANSLATIONS,
    !!custom_texts,
    userInfo.getUserId() && userInfo.user.custom_texts
      ? ThemeTokenTypes.COMPANY
      : ThemeTokenTypes.ORG
  );

  const pdpApi = usePdpLayoutData(
    userInfo.getUserId() && userInfo.user.pdps
      ? userInfo.user.pdps
      : pdps || ApiRoutes.PDP_API,
    !!enabledRoutes.pdps,
    userInfo.getUserId() && userInfo.user.pdps
      ? ThemeTokenTypes.COMPANY
      : ThemeTokenTypes.ORG
  );

  const unifiedProductPagesApi = useUnifiedProductPagesData(
    userInfo.getUserId() && userInfo.user.product_unified_pages
      ? userInfo.user.product_unified_pages
      : product_unified_pages || ApiRoutes.UNIFIED_PRODUCT_PAGES_API,
    !!enabledRoutes.product_unified_pages,
    userInfo.getUserId() && userInfo.user.product_unified_pages
      ? ThemeTokenTypes.COMPANY
      : ThemeTokenTypes.ORG
  );

  const onboardingScreenData = useOnboardingData(
    orgSettings?.data?.data?.organization?.id,
    theme.isLargeScreen
      ? OnboardingScreenPlatform.DESKTOP
      : isTablet
      ? OnboardingScreenPlatform.WEB
      : OnboardingScreenPlatform.MOBILE,
    i18n.language ?? "en",
    !!orgSettings?.data?.data?.organization?.id &&
      !!orgSettings?.data?.data?.init_status?.onboarding_screen,
    OnBoardingTypes.pre_login
  );

  useEffect(() => {
    if (orgSettings.isSuccess) {
      const mEnabledRoutes = {
        custom_texts: false,
        mobile_token_file: false,
        web_token_file: false,
        json_navigation_menus: false,
        pdps: false,
        search_forms: false,
        search_results: false,
        product_unified_pages: false,
        global: false,
      };
      if (
        dataRefreshInfo.isSuccess &&
        dataRefreshInfo.data.data.data &&
        appVersion === Constants?.expoConfig?.version
      ) {
        const {
          custom_texts: _custom_texts,
          mobile_token_file: _mobile_token_file,
          web_token_file: _web_token_file,
          json_navigation_menus: _json_navigation_menus,
          pdps: _pdps,
          search_forms: _search_forms,
          search_results: _search_results,
          product_unified_pages: _product_unified_pages,
          global: _global,
        } = dataRefreshInfo.data?.data?.data || {};

        if (isRefetchRequired(_custom_texts, translationSlice?.updatedTime)) {
          mEnabledRoutes.custom_texts = true;
        }

        if (
          isRefetchRequired(
            _mobile_token_file,
            themeTokensSlice?.updatedTime
          ) &&
          (userInfo.user?.id ? !userInfo.user.custom_theme : true)
        ) {
          mEnabledRoutes.mobile_token_file = true;
        }

        if (
          isRefetchRequired(_web_token_file, themeTokensSlice?.updatedTime) &&
          (userInfo.user?.id ? !userInfo.user.custom_theme : true)
        ) {
          mEnabledRoutes.web_token_file = true;
        }

        if (
          isRefetchRequired(
            _json_navigation_menus,
            navigationMenuSlice.navigationData?.updatedTime
          )
        ) {
          mEnabledRoutes.json_navigation_menus = true;
        }
        if (isRefetchRequired(_global, globalSlice?.updatedTime)) {
          mEnabledRoutes.global = true;
        }
        if (isRefetchRequired(_pdps, pdpSlice?.pdpData?.updatedTime)) {
          mEnabledRoutes.pdps = true;
        }

        if (isRefetchRequired(_search_forms, searchFormsSlice?.updatedTime)) {
          mEnabledRoutes.search_forms = true;
        }

        if (
          isRefetchRequired(_search_results, searchResultsSlice?.updatedTime)
        ) {
          mEnabledRoutes.search_results = true;
        }
        if (
          isRefetchRequired(
            _product_unified_pages,
            unifiedProductPagesSlice.updatedTime
          )
        ) {
          mEnabledRoutes.product_unified_pages = true;
        }
      } else {
        dispatch(setAppVersion(Constants?.expoConfig?.version));
        mEnabledRoutes.custom_texts = true;
        mEnabledRoutes.mobile_token_file = true;
        mEnabledRoutes.web_token_file = true;
        mEnabledRoutes.json_navigation_menus = true;
        mEnabledRoutes.pdps = true;
        mEnabledRoutes.product_unified_pages = true;
        mEnabledRoutes.search_forms = true;
        mEnabledRoutes.search_results = true;
        mEnabledRoutes.global = true;
      }
      setEnabledRoutes(mEnabledRoutes);
      setEnabledChecked(true);
    }
  }, [dataRefreshInfo.data, orgSettings.data]);

  useEffect(() => {
    if (onboardingScreenData?.data?.data?.length) {
      savePreOnboardingData(onboardingScreenData?.data?.data);
    }
  }, [onboardingScreenData.isSuccess]);

  useEffect(() => {
    if (
      enabledChecked &&
      translationApi.isSuccess &&
      dataRefreshInfo.isSuccess &&
      (!!orgSettings?.data?.data?.organization?.id &&
      !!orgSettings?.data?.data?.init_status?.onboarding_screen
        ? onboardingScreenData.isSuccess
        : true) &&
      (enabledRoutes.mobile_token_file || enabledRoutes.web_token_file
        ? themeTokensApi.isSuccess
        : true) &&
      (enabledRoutes.json_navigation_menus
        ? navigationMenuApi.isSuccess
        : true) &&
      (enabledRoutes.global ? globalData.isSuccess : true) &&
      (enabledRoutes.pdps ? pdpApi.isSuccess : true) &&
      (enabledRoutes.product_unified_pages
        ? unifiedProductPagesApi.isSuccess
        : true) &&
      (enabledRoutes.search_forms ? filtersData.isSuccess : true) &&
      (enabledRoutes.search_results
        ? searchResultsCustomization.isSuccess
        : true)
    ) {
      loadTransalations(translationApi?.data?.data, AppStateProps.SUCCESS);
    }
  }, [
    translationApi.isSuccess,
    themeTokensApi.isSuccess,
    navigationMenuApi.isSuccess,
    pdpApi.isSuccess,
    unifiedProductPagesApi.isSuccess,
    enabledChecked,
    fontsReadyForBootstrap,
    filtersData.isSuccess,
    globalData.isSuccess,
    searchResultsCustomization.isSuccess,
    dataRefreshInfo.isSuccess,
    onboardingScreenData.isSuccess,
  ]);

  useEffect(() => {
    if (
      translationApi.isError ||
      themeTokensApi.isError ||
      navigationMenuApi.isError ||
      pdpApi.isError ||
      filtersData.isError ||
      globalData.isError ||
      searchResultsCustomization.isError ||
      dataRefreshInfo.isError ||
      onboardingScreenData.isError
    ) {
      if (translationApi.isError) {
        console.error("Translation API Error:", translationApi.error);
      }
      if (themeTokensApi.isError) {
        console.error("Theme Tokens API Error:", themeTokensApi.error);
      }
      if (navigationMenuApi.isError) {
        console.error("Navigation Menu API Error:", navigationMenuApi.error);
      }
      if (pdpApi.isError) {
        console.error("PDP API Error:", pdpApi.error);
      }
      if (filtersData.isError) {
        console.error("Filters Data API Error:", filtersData.error);
      }
      if (globalData.isError) {
        console.error("Global Data API Error:", globalData.error);
      }
      if (searchResultsCustomization.isError) {
        console.error(
          "Search Results Customization API Error:",
          searchResultsCustomization.error
        );
      }
      if (dataRefreshInfo.isError) {
        console.error("Data Refresh Info API Error:", dataRefreshInfo.error);
      }
      if (onboardingScreenData.isError) {
        console.error(
          "Onboarding Screen Data API Error:",
          onboardingScreenData.error
        );
      }
      loadTransalations(DEFAULT_TRANSLATION_KEYS, AppStateProps.ERROR);
    }
  }, [
    translationApi.isError,
    themeTokensApi.isError,
    navigationMenuApi.isError,
    pdpApi.isError,
    filtersData.isError,
    globalData.isError,
    searchResultsCustomization.isError,
    dataRefreshInfo.isError,
    onboardingScreenData.isError,
  ]);

  const isValidPasswordUpdateUrl = (url: string) => {
    if (!url) return false;

    const containsPath = url?.includes("user/password");

    const urlParams = new URLSearchParams(url.split("?")[1]);
    const token = urlParams.get("token");

    return containsPath && token && token.trim() !== "";
  };

  useEffect(() => {
    if (appStateReady === AppStateProps.SUCCESS) {
      const isOnboardingRequired =
        !onPreBoardingComplete && onboardingScreenData?.data?.data?.length;
      const isMobileOrSmallMobile = isMobile;
      const isDesktopOrTablet = isLargeScreen || isTablet;
      const hasExternalLogin = (url) => {
        const parsedUrl = new URL(url);
        return parsedUrl.searchParams.get("external_login") === "true";
      };
      if (initialUrl && initialUrl.includes("user/confirmation")) {
        setInitialScreen(Routes.VerifiedScreen);
      } else if (isValidPasswordUpdateUrl(initialUrl)) {
        setInitialScreen(Routes.PasswordUpdate);
      } else if (
        Platform.OS === "web" &&
        initialUrl?.includes("forgot-password")
      ) {
        setInitialScreen(Routes.ForgotPassword);
      } else if (
        userInfo.user?.id ||
        (Platform.OS === "web" &&
          localStorage.getItem("userData") &&
          hasExternalLogin(initialUrl))
      ) {
        if (
          Platform.OS === "web" &&
          localStorage.getItem("userData") &&
          hasExternalLogin(initialUrl)
        ) {
          const userData = JSON.parse(localStorage.getItem("userData") || "");
          const token = new URL(initialUrl).searchParams.get("token");
          const installation_identifier = new URL(initialUrl).searchParams.get(
            "installation_identifier"
          );
          dispatch(setUserInfo(userData));
          if (userData && !userData?.public_login) {
            dispatch(setIsGuest(false));
          }
          if (token) {
            localStorage.setItem("token", token);
          }
          if (installation_identifier) {
            localStorage.setItem(
              "installation_identifier",
              installation_identifier
            );
          }
        }
        if (userInfo.hasTermsAccepted() || userInfo.isIFrame()) {
          setInitialScreen(Routes.AppNavigator);
        } else {
          setInitialScreen(Routes.TermsAndConditions);
        }
      } else if (
        (isMobileOrSmallMobile &&
          (isOnboardingRequired ? onboardingScreenData.isSuccess : true)) ||
        isDesktopOrTablet
      ) {
        setInitialScreen(Routes.OnboardingWeb);
      } else if (isMobileOrSmallMobile && onboardingScreenData.isSuccess) {
        setInitialScreen(Routes.Authentication);
      }
    }
  }, [appStateReady, onPreBoardingComplete, onboardingScreenData.isSuccess]);

  const loadTransalations = async (
    translations: any,
    appState: AppStateProps
  ) => {
    const storedLanguage = await AsyncStorage.getItem(
      STORAGE_KEYS.SELECTED_LANGUAGE
    );

    let currentLanguage: string;

    if (initialUrl && initialUrl.includes("lang=")) {
      try {
        const urlParams = new URLSearchParams(initialUrl.split("?")[1] ?? "");
        const langParam = urlParams.get("lang");
        if (langParam) currentLanguage = langParam;
      } catch {}
    } else if (
      storedLanguage &&
      orgSettings?.data?.data?.organization?.supported_language?.some(
        (lan: any) => lan.code === storedLanguage
      )
    ) {
      currentLanguage = storedLanguage;
    } else {
      const locales = getLocales();
      currentLanguage = userInfo.getUserId()
        ? userInfo.user.locale
        : orgSettings?.data?.data?.organization?.supported_language?.findIndex(
            (lan: any) => lan.code === locales?.[0]?.languageCode
          ) >= 0
        ? locales?.[0]?.languageCode
        : orgSettings?.data?.data?.organization?.locale
        ? orgSettings?.data?.data?.organization?.locale
        : "en";
    }

    await initializeI18n(translations, currentLanguage);
    if (isAppInitialLoad) {
      dispatch(setAppInitialLoad());
    }
    if (appState !== AppStateProps.LOADING) {
      try {
        if (Platform.OS === "android") {
          await new Promise((resolve) => setTimeout(resolve, 100));
        }
        await SplashScreenExpo.hideAsync();
        await lockOrientation();
      } catch {}
    }
    setAppStateReady(appState);
  };
  const [isRefetching, setIsRefetching] = useState(false);

  const handleRetry = async () => {
    setIsRefetching(true);
    if (translationApi.isError) await translationApi.refetch();
    if (orgSettings.isError) await orgSettings.refetch();
    if (themeTokensApi.isError) await themeTokensApi.refetch();
    if (dataRefreshInfo.isError) await dataRefreshInfo.refetch();
    if (navigationMenuApi.isError) await navigationMenuApi.refetch();
    if (pdpApi.isError) await pdpApi.refetch();
    if (filtersData.isError) await filtersData.refetch();
    if (globalData.isError) await globalData.refetch();
    if (searchResultsCustomization.isError)
      await searchResultsCustomization.refetch();
    setIsRefetching(false);
  };

  const refetchData = async () => {
    setIsRefetching(true);
    await translationApi.refetch();
    await orgSettings.refetch();
    await themeTokensApi.refetch();
    await dataRefreshInfo.refetch();
    await navigationMenuApi.refetch();
    await pdpApi.refetch();
    await filtersData.refetch();
    await globalData.refetch();

    await searchResultsCustomization.refetch();
    setIsRefetching(false);
  };

  return {
    appStateReady,
    handleRetry,
    isRefetching,
    refetchData,
  };
};
